import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Component, useMemo, type ErrorInfo, type ReactNode } from 'react';
import '@classy/design-tokens/tokens.css';
import './app.css';
import { AuthGate, DevAuthProvider, MsalAuthProvider, resolveMsalConfig } from './auth/index.js';
import { SetupNotice } from './auth/SetupNotice.js';
import { ApiProvider } from './api/ApiProvider.js';
import { ActiveClassroomProvider } from './context/ActiveClassroomProvider.js';
import { createBundledThemeRegistry } from './theme/index.js';
import { PhaseRouter } from './pages/Hub/PhaseRouter.js';
import { AdminPage } from './pages/Admin/AdminPage.js';
import { SessionBg } from './theme/SessionBg.js';
import { LiveMenuBox } from './components/LiveMenuBox.js';
import { LiveBackButton } from './components/LiveBackButton.js';
import { LivePageNav } from './components/LivePageNav.js';
import { LiveClock } from './components/LiveClock.js';
import { MmPageNav } from './components/MmPageNav/index.js';
import { ClockProvider } from './clock/ClockProvider.js';
import { AlarmTakeover } from './clock/AlarmTakeover.js';

interface ErrorBoundaryState {
  error: Error | null;
}

class AppErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[classy] render error', error, info);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          padding: '2rem',
          fontFamily: 'monospace',
          color: '#b03a2e',
          background: '#fff5f3',
          minHeight: '100vh',
          whiteSpace: 'pre-wrap',
        }}
        data-testid="app-error"
      >
        <h2 style={{ marginTop: 0 }}>Something failed during render</h2>
        <p>{error.message}</p>
        <details>
          <summary>Stack</summary>
          {error.stack ?? '(no stack)'}
        </details>
      </div>
    );
  }
}

/**
 * Dev-auth bypass — only honored in dev builds (`import.meta.env.DEV`) AND
 * when `VITE_DEV_AUTH === '1'`. Production builds set `DEV` to false at
 * build time, so the bypass cannot be turned on in a deployed build even if
 * the env var leaks. The backend mirrors this with `CLASSY_DEV_AUTH=1`.
 */
const DEV_AUTH_ON = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1';

// Admin tool deep-link. We keep this as a pathname check (not a router)
// because the rest of the app is phase-driven, not URL-driven. The route
// has no UI link — Sarah bookmarks classy.sm2gk.com/admin.
function isAdminPath(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/');
}

export function App() {
  const msalConfig = useMemo(() => resolveMsalConfig(), []);
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } }),
    [],
  );
  const themeRegistry = useMemo(() => createBundledThemeRegistry(), []);
  const adminRoute = isAdminPath();
  const appClassName = adminRoute ? 'app app--admin' : 'app';

  if (!msalConfig && !DEV_AUTH_ON) {
    return (
      <main data-testid="app-root" className={appClassName}>
        <SetupNotice registry={themeRegistry} />
      </main>
    );
  }

  const innerProviders = adminRoute ? (
    // Admin console — same MSAL auth gate, but bypass PhaseRouter,
    // ActiveClassroomProvider, and all the live-mode chrome.
    <QueryClientProvider client={queryClient}>
      <ApiProvider>
        <AuthGate themeRegistry={themeRegistry}>
          <SessionBg registry={themeRegistry}>
            <AdminPage themeRegistry={themeRegistry} />
          </SessionBg>
        </AuthGate>
      </ApiProvider>
    </QueryClientProvider>
  ) : (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>
        <AuthGate themeRegistry={themeRegistry}>
          <ActiveClassroomProvider>
            {/*
              PhaseRouter picks the right theme + background per view:
                - hub root → session theme (sessionStorage from sign-in)
                - classroom detail → that classroom's theme (localStorage)
                - live morning meeting → that classroom's theme
              Each phase wraps its subtree in a ThemeProvider, so we no
              longer need the app-wide ThemeShell.
            */}
            <ClockProvider>
              <PhaseRouter registry={themeRegistry} />
              {/* Live-mode chrome lives at the App level — outside the
               * KioskStage's transformed container — so position:fixed
               * anchors to the real viewport, not the (possibly
               * letterboxed) stage. Each piece self-hides outside an
               * active classroom. */}
              <LiveBackButton />
              <LivePageNav />
              <MmPageNav />
              <LiveClock />
              <LiveMenuBox />
              <AlarmTakeover />
            </ClockProvider>
          </ActiveClassroomProvider>
        </AuthGate>
      </ApiProvider>
    </QueryClientProvider>
  );

  return (
    <main data-testid="app-root" className={appClassName}>
      <AppErrorBoundary>
        {DEV_AUTH_ON ? (
          <DevAuthProvider>{innerProviders}</DevAuthProvider>
        ) : (
          // msalConfig is guaranteed non-null here — checked above.
          <MsalAuthProvider config={msalConfig!}>{innerProviders}</MsalAuthProvider>
        )}
      </AppErrorBoundary>
    </main>
  );
}
